import { useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Empty, Table } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import CustomPagination from "../custom-component/CustomPagination";
import TableSearchInput from "./TableSearchInput";
import TruncatedText from "./TruncatedText";
import FilterChips, { buildFilterChips, ResetFiltersButton } from "./FilterChips";
import useSearchField from "./useSearchField";
import cn from "../../utilities/tailwind-merge";

// Grouped columns (e.g. Rate → Hourly/Daily/Weekly/Monthly) get a frame so it's clear which
// sub-columns belong to the group header: borders on both sides and under the group title.
const withGroupFrame = (group, children) => {
    const lastIndex = children.length - 1;
    return {
        ...group,
        className: cn(group.className, "sc-col-group sc-col-group-start sc-col-group-end"),
        children: children.map((child, index) => ({
            ...child,
            className: cn(
                child.className,
                "sc-col-group-cell",
                index === 0 && "sc-col-group-start",
                index === lastIndex && "sc-col-group-end",
            ),
        })),
    };
};

// Columns marked `ellipsis` stay on one line and show the full value in a tooltip when cut off.
const withTruncationTooltips = (columns = []) =>
    columns.map((column) => {
        if (column.children) {
            return withGroupFrame(column, withTruncationTooltips(column.children));
        }
        if (!column.ellipsis) return column;

        const { render } = column;
        return {
            ...column,
            ellipsis: { showTitle: false },
            render: (value, record, index) => {
                const content = render ? render(value, record, index) : value;
                return <TruncatedText className="text-sm">{content ?? ""}</TruncatedText>;
            },
        };
    });

/**
 * Standard list table: title + count, toolbar (search, extra filters, Reset filters),
 * active-filter chips, the table itself and a pagination footer, all inside one card.
 *
 * Pass the object returned by `useTableFilters` as `filters` to get the toolbar search,
 * chips and the central reset wired up. Any other props go straight to antd's <Table>.
 */
function DataTable({
    title,
    count,
    headerExtra,
    filters,
    search,
    toolbarExtra,
    filterLabels,
    formatFilterValue,
    pagination,
    emptyText,
    className,
    columns,
    scroll,
    ...tableProps
}) {
    const { t } = useTranslation("table");
    const { searchKey, field, onFieldChange } = useSearchField(search, filters);
    const tableColumns = useMemo(() => withTruncationTooltips(columns), [columns]);

    const chips = filters
        ? buildFilterChips({
              filters: filters.filters,
              columns,
              searchFields: search?.fields,
              searchKey: search?.key,
              filterLabels,
              formatFilterValue,
              t,
          })
        : [];

    const showToolbar = Boolean(search || toolbarExtra || filters);
    const showHeader = Boolean(title || headerExtra);

    const renderEmpty = () => {
        if (filters?.hasActiveFilters) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div className="flex flex-col gap-1">
                            <span className="font-medium text-gray-700">{t("noResults")}</span>
                            <span className="text-xs text-gray-500">{t("noResultsHint")}</span>
                        </div>
                    }>
                    <Button icon={<ReloadOutlined />} onClick={filters.resetFilters}>
                        {t("resetFilters")}
                    </Button>
                </Empty>
            );
        }
        return emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("noData")} />;
    };

    return (
        <section
            className={cn(
                "sc-data-table overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]",
                className,
            )}>
            {showHeader && (
                <header className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4">
                    <div className="flex min-w-0 items-center gap-2">
                        {title && <h2 className="m-0 truncate text-base font-semibold text-gray-900">{title}</h2>}
                        {typeof count === "number" && (
                            <span className="rounded-full bg-[#EEEEFE] px-2 py-0.5 text-xs font-semibold tabular-nums text-[#5D5DF6]">
                                {count.toLocaleString()}
                            </span>
                        )}
                    </div>
                    {headerExtra && <div className="flex flex-wrap items-center gap-2">{headerExtra}</div>}
                </header>
            )}

            {showToolbar && (
                <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-4">
                    {search && filters && (
                        <TableSearchInput
                            value={filters.filters[searchKey] ?? ""}
                            onChange={(text) => filters.setFilter(searchKey, text)}
                            placeholder={search.placeholder}
                            fields={search.fields}
                            field={field}
                            onFieldChange={onFieldChange}
                        />
                    )}
                    {toolbarExtra}
                    {filters && <ResetFiltersButton filters={filters} className="ml-auto" />}
                </div>
            )}

            {filters && (
                <FilterChips
                    className="px-4 pb-3"
                    chips={chips}
                    onRemove={filters.clearFilter}
                    onClearAll={filters.resetFilters}
                />
            )}

            <Table
                columns={tableColumns}
                pagination={false}
                scroll={scroll ?? { x: "max-content" }}
                locale={{ emptyText: renderEmpty() }}
                {...tableProps}
            />

            {pagination && pagination.total > 0 && (
                <footer className="flex justify-end border-t border-[#EEF0F4] px-4 py-3">
                    <CustomPagination
                        totalItem={pagination.total}
                        pageSize={pagination.pageSize}
                        setPageSize={pagination.onPageSizeChange}
                        currentPage={pagination.current}
                        setCurrentPage={pagination.onChange}
                        pageSizeOptions={pagination.pageSizeOptions ?? [10, 20, 50, 100]}
                        showSizeChanger
                    />
                </footer>
            )}
        </section>
    );
}

DataTable.propTypes = {
    title: PropTypes.node,
    count: PropTypes.number,
    headerExtra: PropTypes.node,
    filters: PropTypes.shape({
        filters: PropTypes.object.isRequired,
        activeCount: PropTypes.number.isRequired,
        hasActiveFilters: PropTypes.bool.isRequired,
        setFilter: PropTypes.func.isRequired,
        renameFilter: PropTypes.func.isRequired,
        clearFilter: PropTypes.func.isRequired,
        resetFilters: PropTypes.func.isRequired,
    }),
    search: PropTypes.shape({
        key: PropTypes.string,
        placeholder: PropTypes.string,
        fields: PropTypes.arrayOf(
            PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node.isRequired }),
        ),
    }),
    toolbarExtra: PropTypes.node,
    filterLabels: PropTypes.object,
    formatFilterValue: PropTypes.func,
    pagination: PropTypes.shape({
        total: PropTypes.number.isRequired,
        current: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
        onChange: PropTypes.func.isRequired,
        onPageSizeChange: PropTypes.func.isRequired,
        pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    }),
    emptyText: PropTypes.node,
    className: PropTypes.string,
    columns: PropTypes.array.isRequired,
    scroll: PropTypes.object,
};

export default DataTable;
